import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, RefreshCw, RotateCcw, Search, Sparkles, Undo2, Zap } from "lucide-react";
import { api, errorText } from "../lib/api";
import { fmt } from "../lib/constants";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");
const pct = (v) => (v === null || v === undefined ? "—" : `${Math.round(v * 100)} %`);
const STATUS = {
  applied: { text: "Applied", cls: "bg-slate-100 text-slate-600" },
  uncertain: { text: "Uncertain", cls: "bg-amber-100 text-amber-800" },
  overridden: { text: "Overridden", cls: "bg-live/10 text-live" },
  pending: { text: "Pending", cls: "bg-slate-50 text-slate-400" },
  failed: { text: "Failed", cls: "bg-brand/10 text-brand" },
};
const LABEL_TEXT = { not_a_product: "not a product", not_test: "not a test", test: "test record", not_placeholder: "not a placeholder", placeholder: "placeholder name", poor: "poor name (not a placeholder)", weak: "weak name", ok: "ok name", good: "good name", UNASSIGNED: "Unassigned", unknown: "unknown" };
const label = (v) => (v ? LABEL_TEXT[v] || v : "—");

const Card = ({ c, active, onClick }) => (
  <button type="button" data-testid={`ai-card-${c.case}`} aria-pressed={active} onClick={onClick} className={`panel p-3 text-left transition-colors ${active ? "border-brand ring-2 ring-brand/15" : "hover:border-slate-400"}`}>
    <div className="text-xs font-bold text-ink flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-brand" /> {c.label}</div>
    <div className="mt-1 num text-xl font-bold" data-testid={`ai-card-${c.case}-total`}>{fmt(c.total)}</div>
    <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
      <span className="text-slate-600">applied <b className="num">{fmt(c.applied)}</b></span>
      <span className="text-amber-700">uncertain <b className="num">{fmt(c.uncertain)}</b></span>
      <span className="text-live">overridden <b className="num">{fmt(c.overridden)}</b></span>
      {c.regex > 0 && <span className="text-slate-500" title="Matched by the regex rule – listed so they can be restored">regex rule <b className="num">{fmt(c.regex)}</b></span>}
      {c.pending > 0 && <span className="text-slate-400">pending <b className="num">{fmt(c.pending)}</b></span>}
      {c.failed > 0 && <span className="text-brand">failed <b className="num">{fmt(c.failed)}</b></span>}
    </div>
    <div className="mt-1 text-[11px] text-slate-500 leading-snug">{c.description}{c.threshold ? ` · applied from ${Math.round(c.threshold * 100)} %` : ""}</div>
  </button>
);

const Row = ({ d, options, onOverride, onRemove }) => {
  const [pick, setPick] = useState("");
  const st = d.override ? STATUS.overridden : STATUS[d.status] || STATUS.pending;
  const regex = d.rule === "regex";
  const state = Object.entries(d.state || {}).filter(([k, v]) => v && !["line_item", "name", "deal_name", "company_name", "venue"].includes(k));
  return (
    <div data-testid={`ai-row-${d.id}`} className="px-4 py-2.5 text-sm flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="num shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 w-[118px] truncate" title={d.case_label}>{d.case_label}</span>
      <span className="flex-1 min-w-[220px]">
        <span className="font-semibold text-ink break-words">{d.text}</span>
        <span className="block text-xs text-slate-500">
          {regex ? <span data-testid={`ai-row-regex-${d.id}`} title="Matched by the fixed regex rule ('test' in the name) – no AI call">Regex rule: </span> : "AI: "}<b className="text-ink" data-testid={`ai-row-label-${d.id}`}>{label(d.label)}</b> {d.confidence !== null && d.confidence !== undefined && <span className="num">({pct(d.confidence)})</span>}
          {d.score !== null && d.score !== undefined && <span className="num"> · quality {d.score.toFixed(1)}/4</span>}
          {d.override && <span className="text-live"> → {d.override.label ? `corrected to ${label(d.override.label)}` : "ignored"}{d.override.reason ? ` · “${d.override.reason}”` : ""}</span>}
          {state.length > 0 && <span className="text-slate-400"> · {state.map(([k, v]) => `${k}: ${v}`).join(", ")}</span>}
          {d.error && <span className="text-brand"> · {d.error}</span>}
          <span className="text-slate-400"> · {when(d.decided_at || d.created_at)}</span>
        </span>
      </span>
      <span data-testid={`ai-row-status-${d.id}`} className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${st.cls}`}>{st.text}</span>
      {d.override ? (
        <button type="button" data-testid={`ai-row-restore-${d.id}`} onClick={() => onRemove(d)} className="inline-flex items-center gap-1 rounded-full border border-panel-border px-3 py-1 text-xs text-slate-600 hover:border-slate-400 hover:text-ink transition-colors">
          <RotateCcw className="w-3.5 h-3.5" /> Back to AI
        </button>
      ) : d.status === "pending" ? null : d.case === "test_record" ? (
        d.label === "test" ? (
          <button type="button" data-testid={`ai-row-restore-test-${d.id}`} onClick={() => onOverride(d, { label: "not_test" })} title="This is a real client – count it on the board and in the hygiene checks again (wins over the AI decision and the regex rule)" className="inline-flex items-center gap-1 rounded-full border border-panel-border px-3 py-1 text-xs font-semibold text-slate-700 hover:border-ink hover:text-ink transition-colors">
            <Undo2 className="w-3.5 h-3.5" /> Restore (not a test)
          </button>
        ) : (
          <button type="button" data-testid={`ai-row-exclude-test-${d.id}`} onClick={() => onOverride(d, { label: "test" })} title="Exclude this record from the board and the hygiene checks as a test record" className="inline-flex items-center gap-1 rounded-full border border-panel-border px-3 py-1 text-xs text-slate-600 hover:border-slate-400 hover:text-ink transition-colors">
            <Check className="w-3.5 h-3.5" /> Exclude as test
          </button>
        )
      ) : (
        <span className="inline-flex items-center gap-1">
          <select data-testid={`ai-row-pick-${d.id}`} value={pick} onChange={(e) => setPick(e.target.value)} className="rounded-md border border-panel-border bg-panel px-2 py-1 text-xs text-ink">
            <option value="">Correct to…</option>
            {options.map((o) => <option key={o} value={o}>{label(o)}</option>)}
          </select>
          <button type="button" data-testid={`ai-row-correct-${d.id}`} disabled={!pick} onClick={() => onOverride(d, { label: pick })} className="inline-flex items-center gap-1 rounded-full bg-ink text-page px-3 py-1 text-xs font-semibold hover:bg-ink/85 disabled:opacity-40">
            <Check className="w-3.5 h-3.5" /> Correct
          </button>
          <button type="button" data-testid={`ai-row-suppress-${d.id}`} onClick={() => onOverride(d, { action: "suppress" })} title="Ignore this AI decision – behave as before (regex / Others / Unassigned)" className="inline-flex items-center gap-1 rounded-full border border-panel-border px-3 py-1 text-xs text-slate-600 hover:border-slate-400 hover:text-ink transition-colors">
            <Undo2 className="w-3.5 h-3.5" /> Override
          </button>
        </span>
      )}
    </div>
  );
};

// Admin: everything Jev decided (5 cases) with status, confidence and one-click override / correction
export const AiDecisions = () => {
  const [status, setStatus] = useState(null);
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState({ case: "", status: "", q: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/ai/status"),
        api.get("/admin/ai/decisions", { params: { case: filter.case || undefined, status: filter.status || undefined, q: filter.q || undefined, limit: 150 } }),
      ]);
      setStatus(s.data);
      setList(l.data);
      setError("");
    } catch (e) {
      setError(errorText(e));
    }
  }, [filter]);
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);
  const act = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy("");
    }
  };
  const options = (c) => status?.cases?.find((x) => x.case === c)?.options || [];
  return (
    <section data-testid="ai-section" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-brand" /> AI decisions · Jev</div>
          <p className="text-xs text-slate-500 mt-1 max-w-3xl" data-testid="ai-help">
            Fast typed decisions over short HubSpot texts: unknown line items → product, test-record filter, Venue / Merchant suggestion, country from free text and deal-name quality. Every text is decided once; decisions above the threshold apply automatically, uncertain ones keep today's behaviour. A correction or override here is a manual rule that always wins – also over the fixed regex rule for test records – applied on the next refresh or right away with <i>Apply now</i>. HubSpot itself is never changed.
            {status && <span className="num text-slate-400"> · {fmt(status.decisions_total)} decisions · {fmt(status.tokens_total)} tokens · last {when(status.last_decided_at)}{status.pending ? ` · ${fmt(status.pending)} pending` : ""}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" data-testid="ai-reevaluate" disabled={!!busy} onClick={() => act("re", async () => api.post("/admin/ai/reevaluate"))} className="inline-flex items-center gap-1.5 rounded-full border border-panel-border px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-400 disabled:opacity-50" title="Send every uncertain / failed decision back to Jev">
            <RefreshCw className={`w-3.5 h-3.5 ${busy === "re" ? "animate-spin" : ""}`} /> Re-evaluate uncertain
          </button>
          <button type="button" data-testid="ai-apply" disabled={!!busy} onClick={() => act("apply", async () => api.post("/admin/ai/apply"))} className="inline-flex items-center gap-1.5 rounded-full bg-ink text-page px-3 py-1.5 text-xs font-semibold hover:bg-ink/85 disabled:opacity-50" title="Rebuild the board with the current decisions (no HubSpot calls)">
            <Zap className="w-3.5 h-3.5" /> Apply now
          </button>
        </div>
      </div>
      {status && !status.available && (
        <div className="panel p-3 text-sm text-amber-900 bg-amber-50 border-amber-300 flex items-start gap-2" data-testid="ai-unavailable">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
          <span><b>AI decisions unavailable since {when(status.unavailable_since)}.</b> {status.unavailable_reason} The board runs on the regex rules meanwhile; nothing is invented. Re-evaluate retries.</span>
        </div>
      )}
      {error && <div className="panel p-3 text-sm text-brand" data-testid="ai-error">{error}</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2" data-testid="ai-cards">
        {(status?.cases || []).map((c) => <Card key={c.case} c={c} active={filter.case === c.case} onClick={() => setFilter((f) => ({ ...f, case: f.case === c.case ? "" : c.case }))} />)}
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <label className="flex-1 flex items-center gap-2 rounded-lg border border-panel-border bg-slate-50 px-3 py-1.5 text-sm focus-within:border-brand">
          <Search className="w-4 h-4 text-slate-400" />
          <input data-testid="ai-search" value={filter.q} onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))} placeholder="Search decided texts…" className="flex-1 bg-transparent outline-none text-ink placeholder:text-slate-400" />
        </label>
        <div className="flex gap-1" data-testid="ai-status-filter">
          {["", "applied", "uncertain", "overridden", "pending", "failed"].map((s) => (
            <button key={s || "all"} type="button" data-testid={`ai-filter-${s || "all"}`} aria-pressed={filter.status === s} onClick={() => setFilter((f) => ({ ...f, status: s }))} className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${filter.status === s ? "border-ink bg-ink text-page" : "border-panel-border text-slate-600 hover:border-slate-400"}`}>
              {s ? STATUS[s].text : "All"}
            </button>
          ))}
        </div>
        {list && <span className="num text-xs text-slate-500" data-testid="ai-list-count">{fmt(list.decisions.length)} of {fmt(list.total)}</span>}
      </div>
      <div className="panel divide-y divide-panel-border max-h-[560px] overflow-y-auto thin-scroll" data-testid="ai-list">
        {list && !list.decisions.length && <div className="p-4 text-sm text-slate-500" data-testid="ai-list-empty">No decisions match.</div>}
        {(list?.decisions || []).map((d) => (
          <Row key={d.id} d={d} options={options(d.case)} onOverride={(row, body) => act(row.id, async () => api.post(`/admin/ai/decisions/${row.id}/override`, body))} onRemove={(row) => act(row.id, async () => api.delete(`/admin/ai/decisions/${row.id}/override`))} />
        ))}
      </div>
    </section>
  );
};
